import React, { useCallback, useEffect, useMemo, useState } from "react";
import moment from "moment";
import { toast } from "react-toastify";
import {
  PaDataTable,
  PaEmptyState,
  PaFilterChip,
  PaSkeleton,
  PaStatusBadge,
} from "components/platform-admin";
import { platformRoleAccess } from "helpers/RolesPermission";
import {
  fetchPlatformTenantPaymentHistory,
  payPlatformTenantInvoice,
  sendPlatformTenantInvoiceReminder,
  sendPlatformTenantOutstandingReminder,
} from "store/services/platformAdmin.services";
import { formatCurrency, formatInvoiceStatusLabel } from "../Billing/billingOverview.utils";
import FinancialQueueActionButtons from "../Billing/FinancialQueueActionButtons";
import BillingReminderConfirmModal from "../Billing/BillingReminderConfirmModal";
import { formatBillingTimestamp } from "./tenantBilling.utils";
import TenantActionModal from "./TenantActionModal";
import { countPayableInvoices, isPayableInvoice } from "./tenantInvoice.utils";

const OPEN_STATUSES = new Set(["open", "draft", "uncollectible"]);
const PAID_STATUSES = new Set(["paid"]);

const PAYMENT_FILTERS = [
  { key: "all", label: "All" },
  { key: "outstanding", label: "Outstanding" },
  { key: "overdue", label: "Overdue" },
  { key: "paid", label: "Paid" },
];

const isOverdueInvoice = (invoice) => {
  if (!invoice?.due_date) {
    return false;
  }

  if (PAID_STATUSES.has(invoice.status)) {
    return false;
  }

  return moment(invoice.due_date).isBefore(moment(), "day");
};

const getInvoiceStatusVariant = (status) => {
  switch (status) {
    case "paid":
      return "success";
    case "open":
    case "draft":
      return "warning";
    case "uncollectible":
    case "void":
      return "danger";
    default:
      return "neutral";
  }
};

const SummaryCard = ({ label, value }) => (
  <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
    <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</p>
    <p className="mt-1 text-lg font-semibold text-gray-900">{value}</p>
  </div>
);

const filterInvoices = (invoices, filterKey) => {
  if (filterKey === "outstanding") {
    return invoices.filter(
      (invoice) =>
        OPEN_STATUSES.has(invoice.status) || (invoice.amount_due_cents || 0) > 0
    );
  }

  if (filterKey === "overdue") {
    return invoices.filter(
      (invoice) =>
        isOverdueInvoice(invoice) &&
        (OPEN_STATUSES.has(invoice.status) || (invoice.amount_due_cents || 0) > 0)
    );
  }

  if (filterKey === "paid") {
    return invoices.filter(
      (invoice) => PAID_STATUSES.has(invoice.status) || (invoice.amount_paid_cents || 0) > 0
    );
  }

  return invoices;
};

const TenantPaymentHistorySection = ({
  tenantId,
  history: externalHistory = null,
  loading: externalLoading = null,
  error: externalError = null,
  onRetry = null,
  onHistoryChange = null,
}) => {
  const canViewBilling = platformRoleAccess("platform.billing.view");
  const canManageBilling = platformRoleAccess("platform.billing.manage");
  const isControlled = externalHistory !== null || externalLoading !== null;
  const [loading, setLoading] = useState(!isControlled);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState(externalHistory);
  const [paymentFilter, setPaymentFilter] = useState("all");
  const [actionModal, setActionModal] = useState(null);
  const [reminderModal, setReminderModal] = useState(null);
  const [actionSubmitting, setActionSubmitting] = useState(false);

  const loadHistory = useCallback(async () => {
    if (!canViewBilling || !tenantId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await fetchPlatformTenantPaymentHistory(tenantId);
      setHistory(data);
    } catch (err) {
      setHistory(null);
      setError(err?.message || "Failed to load payment history");
    } finally {
      setLoading(false);
    }
  }, [canViewBilling, tenantId]);

  useEffect(() => {
    if (isControlled) {
      return undefined;
    }

    loadHistory();
  }, [isControlled, loadHistory]);

  useEffect(() => {
    if (isControlled) {
      setHistory(externalHistory);
    }
  }, [externalHistory, isControlled]);

  const resolvedLoading = isControlled ? Boolean(externalLoading) : loading;
  const resolvedError = isControlled ? externalError : error;
  const resolvedHistory = isControlled ? externalHistory : history;

  const invoiceRows = useMemo(() => {
    const invoices = (resolvedHistory?.invoices || []).map((invoice) => ({
      ...invoice,
      id: invoice.id,
    }));

    return filterInvoices(invoices, paymentFilter);
  }, [resolvedHistory, paymentFilter]);

  const localRecordRows = useMemo(
    () => resolvedHistory?.local_records || [],
    [resolvedHistory]
  );

  const summary = resolvedHistory?.summary;
  const currency = resolvedHistory?.currency || summary?.currency || "AUD";
  const payableInvoiceCount = useMemo(
    () => countPayableInvoices(resolvedHistory?.invoices || []),
    [resolvedHistory]
  );

  const applyHistory = useCallback(
    (nextHistory) => {
      if (onHistoryChange) {
        onHistoryChange(nextHistory);
      } else {
        setHistory(nextHistory);
      }
    },
    [onHistoryChange]
  );

  const handleRetry = onRetry || loadHistory;

  const openInvoiceAction = (type, invoice) => {
    const amountLabel = formatCurrency(invoice.amount_due_cents, invoice.currency || currency);
    const invoiceLabel = invoice.number || invoice.id;

    if (type === "pay") {
      setActionModal({
        type: "pay",
        invoice,
        title: "Process payment in Stripe",
        description: `Attempt to charge the customer's default payment method for invoice ${invoiceLabel} (${amountLabel}).`,
        confirmLabel: "Process payment",
        destructive: false,
      });
      return;
    }

    setReminderModal({
      type: "remind",
      invoice,
      title: "Send payment reminder email",
      description: `Email the tenant billing contact with invoice ${invoiceLabel} (${amountLabel}), pay link, and PDF attachment when available.`,
      confirmLabel: "Send reminder",
    });
  };

  const openOutstandingReminder = () => {
    setReminderModal({
      type: "remind_all",
      title: "Email all outstanding invoices",
      description: `Send one reminder email listing ${payableInvoiceCount} open invoice(s) totalling ${formatCurrency(summary?.outstanding_cents, currency)} with pay links and PDF attachments.`,
      confirmLabel: "Send reminder email",
    });
  };

  const handleActionSubmit = async (reason) => {
    if (!actionModal || !tenantId || actionModal.type !== "pay") {
      return;
    }

    setActionSubmitting(true);

    try {
      const result = await payPlatformTenantInvoice(tenantId, actionModal.invoice.id, reason);
      toast.success("Stripe payment processed");
      if (result?.payment_history) {
        applyHistory(result.payment_history);
      } else {
        await handleRetry();
      }

      setActionModal(null);
    } catch (err) {
      toast.error(err?.message || "Billing action failed");
    } finally {
      setActionSubmitting(false);
    }
  };

  const handleReminderConfirm = async () => {
    if (!reminderModal || !tenantId) {
      return;
    }

    setActionSubmitting(true);

    try {
      if (reminderModal.type === "remind") {
        const result = await sendPlatformTenantInvoiceReminder(
          tenantId,
          reminderModal.invoice.id
        );
        toast.success(`Reminder sent to ${result?.recipient_email || "billing contact"}`);
      } else {
        const result = await sendPlatformTenantOutstandingReminder(tenantId);
        toast.success(
          `Reminder sent for ${result?.invoice_count || payableInvoiceCount} invoice(s) to ${
            result?.recipient_email || "billing contact"
          }`
        );
      }

      setReminderModal(null);
    } catch (err) {
      toast.error(err?.message || "Billing action failed");
    } finally {
      setActionSubmitting(false);
    }
  };

  if (!canViewBilling) {
    return null;
  }

  if (resolvedLoading) {
    return <PaSkeleton variant="table" lines={5} />;
  }

  if (resolvedError) {
    return (
      <div className="space-y-3">
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {resolvedError}
        </div>
        <button
          type="button"
          onClick={handleRetry}
          className="text-sm font-medium text-indigo-600 hover:text-indigo-700"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!resolvedHistory?.has_stripe_customer) {
    return (
      <PaEmptyState
        title="No Stripe customer"
        message="Payment history requires a Stripe customer on this tenant."
      />
    );
  }

  return (
    <section className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-base font-semibold text-gray-900">Payment history</h2>
          <p className="mt-1 text-sm text-gray-500">
            Live invoice data from Stripe for customer {resolvedHistory.stripe_customer_id}.
          </p>
        </div>
        {canManageBilling && payableInvoiceCount > 0 ? (
          <button
            type="button"
            onClick={openOutstandingReminder}
            className="inline-flex items-center rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm font-medium text-indigo-700 shadow-sm transition-colors hover:bg-indigo-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-1"
          >
            Email outstanding reminder
          </button>
        ) : null}
      </div>

      {summary ? (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
          <SummaryCard
            label="Collected (all time)"
            value={formatCurrency(summary.collected_all_time_cents, currency)}
          />
          <SummaryCard
            label="Collected (30 days)"
            value={formatCurrency(summary.collected_last_30_days_cents, currency)}
          />
          <SummaryCard
            label="Outstanding"
            value={formatCurrency(summary.outstanding_cents, currency)}
          />
          <SummaryCard
            label="Overdue"
            value={formatCurrency(summary.overdue_cents || 0, currency)}
          />
          <SummaryCard
            label="Next charge"
            value={
              summary.upcoming_amount_cents
                ? formatCurrency(summary.upcoming_amount_cents, currency)
                : "—"
            }
          />
        </div>
      ) : null}

      {resolvedHistory.upcoming_invoice ? (
        <div className="rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-3 text-sm text-indigo-900">
          Upcoming invoice{" "}
          {summary?.upcoming_at
            ? `for period ending ${formatBillingTimestamp(summary.upcoming_at)}`
            : "scheduled"}
          {summary?.upcoming_amount_cents
            ? ` — ${formatCurrency(summary.upcoming_amount_cents, currency)}`
            : ""}
        </div>
      ) : null}

      <div className="flex flex-wrap gap-2">
        {PAYMENT_FILTERS.map((filter) => (
          <PaFilterChip
            key={filter.key}
            active={paymentFilter === filter.key}
            onClick={() => setPaymentFilter(filter.key)}
          >
            {filter.label}
          </PaFilterChip>
        ))}
      </div>

      {invoiceRows.length === 0 ? (
        <PaEmptyState
          title="No invoices"
          message={
            paymentFilter === "all"
              ? "Stripe returned no invoice history for this customer."
              : `No ${paymentFilter} invoices for this customer.`
          }
        />
      ) : (
        <PaDataTable
          columns={[
            {
              key: "created_at",
              label: "Date",
              render: (row) =>
                row.created_at ? moment(row.created_at).format("D MMM YYYY") : "—",
            },
            {
              key: "number",
              label: "Invoice",
              render: (row) => row.number || row.id || "—",
            },
            {
              key: "status",
              label: "Status",
              render: (row) => (
                <PaStatusBadge
                  label={formatInvoiceStatusLabel(row.status)}
                  variant={getInvoiceStatusVariant(row.status)}
                />
              ),
            },
            {
              key: "due_date",
              label: "Due date",
              render: (row) =>
                row.due_date ? moment(row.due_date).format("D MMM YYYY") : "—",
            },
            {
              key: "period_end_at",
              label: "Period end",
              render: (row) =>
                row.period_end_at ? moment(row.period_end_at).format("D MMM YYYY") : "—",
            },
            {
              key: "total_cents",
              label: "Total",
              headerClassName: "text-right",
              cellClassName: "text-right",
              render: (row) => formatCurrency(row.total_cents, row.currency || currency),
            },
            {
              key: "amount_paid_cents",
              label: "Paid",
              headerClassName: "text-right",
              cellClassName: "text-right",
              render: (row) => formatCurrency(row.amount_paid_cents, row.currency || currency),
            },
            {
              key: "amount_due_cents",
              label: "Due",
              headerClassName: "text-right",
              cellClassName: "text-right",
              render: (row) => formatCurrency(row.amount_due_cents, row.currency || currency),
            },
            {
              key: "links",
              label: "",
              render: (row) => (
                <div className="flex flex-col items-end gap-2">
                  <div className="flex justify-end gap-2">
                    {row.hosted_invoice_url ? (
                      <a
                        href={row.hosted_invoice_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-sm text-indigo-600 hover:text-indigo-700"
                      >
                        View
                      </a>
                    ) : null}
                    {row.invoice_pdf ? (
                      <a
                        href={row.invoice_pdf}
                        target="_blank"
                        rel="noreferrer"
                        className="text-sm text-indigo-600 hover:text-indigo-700"
                      >
                        PDF
                      </a>
                    ) : null}
                  </div>
                  {canManageBilling && isPayableInvoice(row) ? (
                    <FinancialQueueActionButtons
                      layout="stack"
                      row={row}
                      onAction={(actionId, invoiceRow) => openInvoiceAction(actionId.replace("invoice_", ""), invoiceRow)}
                      actions={[
                        {
                          id: "invoice_pay",
                          label: "Process payment",
                          title: "Charge the customer's default payment method in Stripe",
                          variant: "success",
                        },
                        {
                          id: "invoice_remind",
                          label: "Send reminder",
                          title: "Email invoice details with pay link and PDF attachment",
                          variant: "primary",
                        },
                      ]}
                    />
                  ) : null}
                </div>
              ),
            },
          ]}
          rows={invoiceRows}
          rowKey="id"
        />
      )}

      {localRecordRows.length > 0 ? (
        <div className="space-y-3 border-t border-gray-100 pt-6">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Local sync records</h3>
            <p className="mt-1 text-xs text-gray-500">
              Webhook and sync events stored in Vertex360 for audit and reconciliation.
            </p>
          </div>
          <PaDataTable
            columns={[
              {
                key: "recorded_at",
                label: "Recorded",
                render: (row) =>
                  row.recorded_at ? moment(row.recorded_at).format("D MMM YYYY, h:mm a") : "—",
              },
              {
                key: "invoice_id",
                label: "Invoice ID",
                render: (row) => (
                  <span className="font-mono text-xs">{row.invoice_id || "—"}</span>
                ),
              },
              {
                key: "invoice_status",
                label: "Status",
                render: (row) => (
                  <PaStatusBadge
                    label={formatInvoiceStatusLabel(row.invoice_status)}
                    variant={getInvoiceStatusVariant(row.invoice_status)}
                  />
                ),
              },
              {
                key: "total_cents",
                label: "Total",
                headerClassName: "text-right",
                cellClassName: "text-right",
                render: (row) => formatCurrency(row.total_cents, row.currency || currency),
              },
              {
                key: "amount_due_cents",
                label: "Due",
                headerClassName: "text-right",
                cellClassName: "text-right",
                render: (row) => formatCurrency(row.amount_due_cents, row.currency || currency),
              },
            ]}
            rows={localRecordRows}
            rowKey="id"
          />
        </div>
      ) : null}

      <TenantActionModal
        open={Boolean(actionModal)}
        onClose={() => setActionModal(null)}
        title={actionModal?.title}
        description={actionModal?.description}
        confirmLabel={actionModal?.confirmLabel}
        destructive={actionModal?.destructive}
        submitting={actionSubmitting}
        onSubmit={handleActionSubmit}
      />

      <BillingReminderConfirmModal
        open={Boolean(reminderModal)}
        onClose={() => setReminderModal(null)}
        title={reminderModal?.title}
        description={reminderModal?.description}
        confirmLabel={reminderModal?.confirmLabel}
        submitting={actionSubmitting}
        onConfirm={handleReminderConfirm}
      />
    </section>
  );
};

export default TenantPaymentHistorySection;
